import React, { createContext, useContext, useState } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { llmClassifierVersionLabel } from "../utils/llmClassifierVersionDisplay";
import { useAddPromptParentsConfirmation } from "./ChainSource/useAddPromptParentsConfirmation";
import { SaveLLMClassifierConfirmationDialog } from "./SaveLLMClassifierConfirmationDialog";

/** What a just-completed save created, for the post-save confirmation banner. */
export interface JustSavedInfo {
    /** The version this save created; once the loaded version moves off of it, the banner clears. */
    newVersionId: string;
    newVersionLabel?: string;
    /** The version saved from (branch source) — always the version that was loaded pre-save. */
    previousLabel?: string;
}

interface SaveFlow {
    /** Runs the save flow (parent-model confirmation, then the changes-review dialog or an
     *  immediate save for a never-saved classifier); `onSaved` runs once the save lands. */
    triggerSave: (onSaved?: () => void) => void;
    isSaving: boolean;
    justSaved?: JustSavedInfo;
    dismissJustSaved: () => void;
}

const SaveFlowContext = createContext<SaveFlow | undefined>(undefined);

export function useSaveFlow(): SaveFlow {
    const context = useContext(SaveFlowContext);
    if (!context) {
        throw new Error(
            "useSaveFlow must be used within SaveFlowContext.Provider",
        );
    }
    return context;
}

/**
 * Owns the one save flow so every entry point that can trigger a save — the toolbar's Save
 * button, the version switcher's "Save Model first", and the navigation guard — goes through
 * the exact same validation and confirmation dialog instead of each reimplementing it. Called
 * once at the top of the builder page; expose the result to descendants via
 * `SaveFlowContext.Provider` and render `dialogs` alongside the page content.
 */
export function useCreateSaveFlow(): SaveFlow & { dialogs: React.ReactNode } {
    const store = useStore(LLMClassifierBuilderStore);
    const {
        confirmConfigurationParents,
        confirmationDialog: addParentsDialog,
    } = useAddPromptParentsConfirmation();

    const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
    const [versionDescription, setVersionDescription] = useState("");
    const [onSaved, setOnSaved] = useState<(() => void) | undefined>();
    const [justSaved, setJustSaved] = useState<JustSavedInfo>();

    const isSaving = store.getTaskLoading(
        LLMClassifierBuilderStore.Tasks.SaveLLMClassifier,
    );
    const isPublished = store.classifier.isPublished === true;
    const willTemplateChange =
        store.isEditing &&
        store.classifier.implementation.isStandard() &&
        store.classifier.implementation.standardBuilder.willTemplateChange();
    // The review dialog is where the old and new values are worth spelling out in full.
    const saveChanges = store.getSaveChanges({ includeLongTextValues: true });
    const willPublishedGroupChange =
        isPublished && saveChanges?.hasGroupChange === true;

    // Captured before the save moves the editor onto the newly created version.
    const captureBranchInfo = (): Pick<JustSavedInfo, "previousLabel"> => ({
        previousLabel: llmClassifierVersionLabel(
            store.classifier.implementation.createdOn,
        ),
    });

    const triggerSave = (onSavedCallback?: () => void) => {
        const save = () => {
            if (store.isSaved) {
                setOnSaved(() => onSavedCallback);
                setIsSaveDialogOpen(true);
            } else {
                const branchInfo = captureBranchInfo();
                void store.saveLLMClassifier().then((result) => {
                    if (!result) return;

                    setJustSaved({
                        newVersionId: store.classifier.implementation.id,
                        newVersionLabel: llmClassifierVersionLabel(
                            store.classifier.implementation.createdOn,
                        ),
                        ...branchInfo,
                    });
                    onSavedCallback?.();
                });
            }
        };

        // Prompts can reference a model that was never declared as a parent, which the save
        // rejects. Offer to add them here rather than after the version has been described.
        if (confirmConfigurationParents(save)) return;
        save();
    };

    const dialogs = (
        <>
            {addParentsDialog}
            <SaveLLMClassifierConfirmationDialog
                isOpen={isSaveDialogOpen}
                isLoading={isSaving}
                willTemplateChange={willTemplateChange}
                willPublishedGroupChange={willPublishedGroupChange}
                versionDescription={versionDescription}
                changes={saveChanges}
                onVersionDescriptionChange={setVersionDescription}
                onClose={() => setIsSaveDialogOpen(false)}
                onConfirm={async () => {
                    const branchInfo = captureBranchInfo();
                    const result = await store.saveLLMClassifier(
                        versionDescription,
                    );
                    if (!result) return;

                    setVersionDescription("");
                    setIsSaveDialogOpen(false);
                    setJustSaved({
                        newVersionId: store.classifier.implementation.id,
                        newVersionLabel: llmClassifierVersionLabel(
                            store.classifier.implementation.createdOn,
                        ),
                        ...branchInfo,
                    });
                    onSaved?.();
                }}
            />
        </>
    );

    return {
        triggerSave,
        isSaving,
        justSaved,
        dismissJustSaved: () => setJustSaved(undefined),
        dialogs,
    };
}

export { SaveFlowContext };
